import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAPI } from 'src/hooks';
import { useDebouncedCallback } from 'use-debounce';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { user } from '@state/user';
import { web3 } from '@state/web3';
import loadable from '@loadable/component';

const ErrorMsg = loadable(() => import('@com/utils/ErrorMsg'));
const Footer = loadable(() => import('@com/footer/Footer'));
const Header = loadable(() => import('@com/header/Header'));
const TopSection = loadable(() => import('@com/TopSection'));

interface FormInputs {
  email: string;
  password: string;
}

function Login(): JSX.Element {
  /* Define state / hook */
  const navigate = useNavigate();
  const { state } = useLocation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>();
  const { t, ready } = useTranslation();
  const { connect } = web3.useContainer();
  const { emailLogin } = useAPI();
  const { userData, setUser, setIsLogin, updateMetamaskStatus } =
    user.useContainer();

  /* Custom Data Object for render or other usages */

  /* Render control function / logic */
  const onSubmit = useDebouncedCallback(
    async (data: FormInputs) => {
      const response = await emailLogin(data.email, data.password);
      if (response.token) {
        setUser(response);
        setIsLogin(true);
        updateMetamaskStatus(response);
        navigate(state?.redirectTo || '/profile/my');
      }
    },
    500,
    { leading: true, trailing: false }
  );

  /* Data Change Listeners - useEffect/useMemo/useCallback */
  useEffect(() => {
    // FIXME: If not detect isInitialized, will show key directly (lazy load issue?)
    if (userData && ready) {
      toast.info(t('toastMessage.alreadyLogin'));
      navigate('/');
    }
  }, [userData, ready]);

  /* Styling */

  /* Render */
  return (
    <>
      <Header />
      <TopSection
        sectionName={t('loginPage.loginText', {
          name: process.env.REACT_APP_SHORT_TITLE,
        })}
      />
      <section className="tf-login tf-section content-container">
        <div className="themesflat-container">
          <div className="p-5">
            <div className="mb-5">
              <div className="flat-form box-login-social">
                <button
                  className="sc-button style-2 fl-button pri-3 mb-5"
                  type="button"
                  onClick={() => connect((state as any)?.redirectTo)}
                >
                  <i className="fas fa-sign-in-alt" />
                  <span> {t('loginPage.loginWithMetaMask')}</span>
                </button>
              </div>
            </div>
            <div className="row center">
              <div className="w-100 flat-form flex">
                <div className="col-5 line" />
                <div className="col-2 fa-2x">{t('loginPage.or')}</div>
                <div className="col-5 line" />
              </div>
            </div>
            <div className="flat-form box-login-email">
              <div className="form-inner">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <input
                    aria-required
                    aria-label="email"
                    autoComplete="email"
                    data-test="email-input"
                    placeholder={t('loginPage.yourEmailAddress')}
                    type="text"
                    {...register('email', {
                      required: {
                        value: true,
                        message: t('errorMsg.login.emailRequired'),
                      },
                      pattern: {
                        value: /^\s*[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\s*$/i,
                        message: t('errorMsg.login.emailPattern'),
                      },
                    })}
                  />
                  <ErrorMsg errors={errors} name="email" />

                  <input
                    aria-required
                    aria-label="password"
                    autoComplete="current-password"
                    data-test="password-input"
                    placeholder={t('loginPage.yourPassword')}
                    type="password"
                    {...register('password', {
                      required: {
                        value: true,
                        message: t('errorMsg.login.passwordRequired'),
                      },
                    })}
                  />
                  <ErrorMsg errors={errors} name="password" />
                  <div className="row-form style-1 mt-5">
                    <Link className="forgot-pass h3" to="/register">
                      <i className="fas fa-globe" />
                      <span className="ml-3">{t('loginPage.signUp')}</span>
                    </Link>
                    {/* <label>
                        {t('signUpPage.rememberMe')}
                        <input type="checkbox" />
                        <span className="btn-checkbox" />
                      </label> */}
                    <Link
                      className="forgot-pass h3"
                      data-test="forgot-password"
                      to="/forget-password"
                    >
                      {t('signUpPage.forgotPassword')}
                    </Link>
                  </div>
                  <button className="mt-3" data-test="login-btn" type="submit">
                    {t('loginPage.login')}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}

export default Login;
